import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { fetchOrderSummary, fetchOrderPhotos, track } from '../../lib/shopApi';
import { fmt } from '../../lib/shopPricing';
import { useCart } from '../../context/CartContext';
import { businessInfo } from '../../mockData';
import usePageMeta from '../../hooks/usePageMeta';
import OrderPhotosUpload from '../../components/shop/OrderPhotosUpload';

export const OrderSuccessPage = () => {
  usePageMeta();
  const [params] = useSearchParams();
  const { clear } = useCart();
  const orderNo = params.get('order') || '';
  const [state, setState] = useState({ loading: true, order: null, error: '' });

  useEffect(() => {
    let cancelled = false;
    if (!orderNo) {
      setState({ loading: false, order: null, error: 'No order reference in the link.' });
      return undefined;
    }
    const q = {};
    if (params.get('session_id')) q.session_id = params.get('session_id');
    if (params.get('token')) q.token = params.get('token');
    if (params.get('test')) q.test = params.get('test');
    fetchOrderSummary(orderNo, q)
      .then((order) => {
        if (cancelled) return;
        setState({ loading: false, order, error: '' });
        if (order && order.status && order.status !== 'pending') {
          clear();
          track('purchase', { order: order.orderNo, total: order.totalCents / 100, install: !!order.install });
        }
      })
      .catch((err) => {
        if (!cancelled) setState({ loading: false, order: null, error: err.message || 'Could not load the order.' });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNo]);

  const { loading, order, error } = state;
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {loading ? (
          <p className="flex items-center gap-2 text-gray-700">
            <Loader2 className="animate-spin" size={18} /> Confirming your payment…
          </p>
        ) : error ? (
          <>
            <h1 className="text-3xl font-bold text-gray-900 mb-3">We could not confirm the order online</h1>
            <p className="text-gray-700 mb-2">{error}</p>
            <p className="text-gray-700">
              If you were charged you will still get a confirmation email. Otherwise call {businessInfo.phone} and quote {orderNo || 'your order'}.
            </p>
          </>
        ) : (
          <>
            <p className="flex items-center gap-2 text-green-700 font-semibold mb-2">
              <CheckCircle size={20} /> {order.status === 'pending' ? 'Payment is being confirmed' : 'Order received'}
            </p>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" data-testid="order-no">
              Thanks — order {order.orderNo}
            </h1>
            <p className="text-gray-700 mb-6">A confirmation with your GST receipt is on its way to {order.email}.</p>
            {/* The photo ask comes here, after the money — never in the cart (Hugh, 17 Sep 2026).
                Any order that needs a size: an install, or a shipped lock that comes in sizes. */}
            {order.photoToken && order.photosStatus === 'outstanding' && (
              <div className="mb-8">
                <OrderPhotosUpload orderNo={order.orderNo} token={order.photoToken} received={order.photoCount || 0} next={order.install ? 'book a time' : 'send it'} />
              </div>
            )}
            <ul className="border border-gray-200 rounded-xl divide-y divide-gray-200 mb-6">
              {order.items.map((it) => (
                <li key={`${it.variantId}-${it.backsetMm || ''}-${it.install ? 'fit' : 'ship'}`} className="p-4 flex justify-between gap-4 text-sm">
                  <span className="text-gray-900">
                    {it.qty} × {it.name}
                    {it.variantLabel ? ` — ${it.variantLabel}` : ''}
                    {it.backsetMm ? ` · ${it.backsetMm} mm` : ''}
                    {it.install ? ' · supply & install' : ''}
                  </span>
                  <span className="text-gray-900 whitespace-nowrap">{fmt(it.lineCents)}</span>
                </li>
              ))}
              {order.install && (
                <li className="p-4 flex justify-between gap-4 text-sm">
                  <span className="text-gray-900">
                    Installation — {order.install.tierLabel} ({order.install.suburb})
                  </span>
                  <span className="whitespace-nowrap">{fmt(order.install.cents + (order.install.travelCents || 0))}</span>
                </li>
              )}
              <li className="p-4 flex justify-between gap-4 font-bold">
                <span>Total incl. GST</span>
                <span>{fmt(order.totalCents)}</span>
              </li>
            </ul>
            <h2 className="text-xl font-bold text-gray-900 mb-2">What happens next</h2>
            {order.install ? (
              <ol className="list-decimal pl-6 text-gray-700 space-y-1 mb-6">
                <li>Your photos of the door — above, or from the link in your confirmation email.</li>
                <li>We check the fit (or suggest a better lock) within one working day of the photos.</li>
                <li>You get a text with a booking link to pick a time, and our installer arrives with the lock.</li>
              </ol>
            ) : (
              <ol className="list-decimal pl-6 text-gray-700 space-y-1 mb-6">
                {order.photosStatus === 'outstanding'
                  ? <li>Your photos of the door — above, or from the link in your confirmation email — so we can confirm the size. Then your order goes to the supplier and is dispatched within the lead time shown on the product.</li>
                  : <li>Your order goes to the supplier straight away and is dispatched within the lead time shown on the product.</li>}
                <li>We email you the NZ Post tracking number as soon as it is on its way. Signature required on delivery.</li>
              </ol>
            )}
            <Link to="/shop">
              <Button variant="outline" className="border-gray-900 text-gray-900 font-semibold">
                Back to the shop <ArrowRight className="ml-2" size={16} />
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export const OrderCancelledPage = () => {
  usePageMeta();
  const [params] = useSearchParams();
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h1 className="text-3xl font-bold text-gray-900 mb-3">Payment cancelled</h1>
        <p className="text-gray-700 mb-6">
          Nothing was charged{params.get('order') ? ` for ${params.get('order')}` : ''}. Your cart is saved, so you can try again or pay a different way.
        </p>
        <Link to="/shop/cart">
          <Button className="bg-red-600 hover:bg-red-700 text-white font-bold">
            Back to your cart <ArrowRight className="ml-2" size={16} />
          </Button>
        </Link>
      </div>
    </div>
  );
};

// /shop/order/photos?order=SD-…&t=… — the link in the confirmation email and the reminders.
// Same upload block as the success page, for whoever paid on a desktop or left it for later.
export const OrderPhotosPage = () => {
  usePageMeta();
  const [params] = useSearchParams();
  const orderNo = params.get('order') || '';
  const token = params.get('t') || '';
  const [state, setState] = useState({ loading: true, order: null, error: '' });

  useEffect(() => {
    let cancelled = false;
    if (!orderNo || !token) {
      setState({ loading: false, order: null, error: 'This link is missing its order reference — open the link from your confirmation email.' });
      return undefined;
    }
    fetchOrderPhotos(orderNo, token)
      .then((order) => { if (!cancelled) setState({ loading: false, order, error: '' }); })
      .catch((err) => { if (!cancelled) setState({ loading: false, order: null, error: err.message || 'Could not load the order.' }); });
    return () => { cancelled = true; };
  }, [orderNo, token]);

  const { loading, order, error } = state;
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {loading ? (
          <p className="flex items-center gap-2 text-gray-700"><Loader2 className="animate-spin" size={18} /> Loading your order…</p>
        ) : error ? (
          <>
            <h1 className="text-3xl font-bold text-gray-900 mb-3">We could not open that order</h1>
            <p className="text-gray-700 mb-2">{error}</p>
            <p className="text-gray-700">Reply to your confirmation email with the photos instead, or call {businessInfo.phone}.</p>
          </>
        ) : (
          <>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2" data-testid="order-photos-no">Order {order.orderNo}</h1>
            <p className="text-gray-700 mb-6">{order.summary}{order.install ? ` · installation in ${order.install.suburb}` : ''}</p>
            {order.photosStatus === 'received' ? (
              <div className="rounded-2xl border border-green-200 bg-green-50 p-5 text-green-800" role="status" data-testid="order-photos-done">
                <p className="flex items-start gap-2 font-semibold"><CheckCircle className="mt-0.5 shrink-0" size={18} /> {order.photoCount} photo{order.photoCount === 1 ? '' : 's'} received — nothing more to send.</p>
                <p className="text-sm mt-2">We check the fit and {order.next || 'book a time'} within one working day.</p>
              </div>
            ) : (
              <OrderPhotosUpload orderNo={order.orderNo} token={token} received={order.photoCount || 0} next={order.next || 'book a time'} />
            )}
          </>
        )}
      </div>
    </div>
  );
};
